import { Extension, type Editor, type Range } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";

// Notion-style "/" block menu for the guide editor. Self-contained: renders a
// plain-DOM dropdown positioned at the caret (no ReactRenderer/tippy dep) and
// handles arrow/enter keyboard nav. Each item runs an editor chain to insert a block.

interface SlashItem {
  title: string;
  hint: string;
  run: (editor: Editor, range: Range) => void;
}

const SLASH_ITEMS: SlashItem[] = [
  { title: "Heading 2", hint: "Section title", run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 2 }).run() },
  { title: "Heading 3", hint: "Subsection", run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 3 }).run() },
  { title: "Bullet list", hint: "Unordered list", run: (e, r) => e.chain().focus().deleteRange(r).toggleBulletList().run() },
  { title: "Numbered list", hint: "Ordered list", run: (e, r) => e.chain().focus().deleteRange(r).toggleOrderedList().run() },
  { title: "Checklist", hint: "To-do items", run: (e, r) => e.chain().focus().deleteRange(r).toggleTaskList().run() },
  { title: "Quote", hint: "Callout / tip", run: (e, r) => e.chain().focus().deleteRange(r).toggleBlockquote().run() },
  { title: "Table", hint: "3×3 with header", run: (e, r) => e.chain().focus().deleteRange(r).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { title: "Divider", hint: "Horizontal rule", run: (e, r) => e.chain().focus().deleteRange(r).setHorizontalRule().run() },
];

export const SlashCommand = Extension.create({
  name: "slashCommand",

  addProseMirrorPlugins() {
    return [
      Suggestion<SlashItem>({
        editor: this.editor,
        char: "/",
        allowSpaces: false,
        startOfLine: false,
        items: ({ query }) =>
          SLASH_ITEMS.filter((item) => item.title.toLowerCase().includes(query.toLowerCase())).slice(0, 8),
        command: ({ editor, range, props }) => props.run(editor, range),
        render: () => {
          let menu: HTMLDivElement | null = null;
          let items: SlashItem[] = [];
          let selected = 0;
          let cmd: ((item: SlashItem) => void) | null = null;

          const paint = () => {
            if (!menu) return;
            menu.innerHTML = "";
            items.forEach((item, i) => {
              const row = document.createElement("button");
              row.type = "button";
              row.className =
                "tw-flex tw-w-full tw-flex-col tw-items-start tw-rounded tw-px-2.5 tw-py-1.5 tw-text-left " +
                (i === selected
                  ? "tw-bg-violet-600 tw-text-white"
                  : "tw-text-gray-700 hover:tw-bg-gray-100");
              row.innerHTML = `<span class="tw-text-sm tw-font-medium">${item.title}</span><span class="tw-text-[11px] ${i === selected ? "tw-text-violet-100" : "tw-text-gray-400"}">${item.hint}</span>`;
              row.addEventListener("mousedown", (e) => {
                e.preventDefault();
                cmd?.(item);
              });
              menu!.appendChild(row);
            });
            // Keep the highlighted row visible when navigating past the menu's max height.
            (menu.children[selected] as HTMLElement | undefined)?.scrollIntoView({ block: "nearest" });
          };

          const position = (rect: (() => DOMRect | null) | null | undefined) => {
            if (!menu || !rect) return;
            const r = rect();
            if (!r) return;
            menu.style.top = `${r.bottom + window.scrollY + 4}px`;
            menu.style.left = `${r.left + window.scrollX}px`;
          };

          return {
            onStart: (props) => {
              items = props.items;
              selected = 0;
              cmd = (item) => props.command(item);
              menu = document.createElement("div");
              menu.className =
                "tw-absolute tw-z-[60] tw-max-h-72 tw-w-56 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-1 tw-shadow-lg";
              document.body.appendChild(menu);
              paint();
              position(props.clientRect);
            },
            onUpdate: (props) => {
              items = props.items;
              selected = 0;
              cmd = (item) => props.command(item);
              paint();
              position(props.clientRect);
            },
            onKeyDown: (props) => {
              if (!items.length) return false;
              if (props.event.key === "ArrowDown") {
                selected = (selected + 1) % items.length;
                paint();
                return true;
              }
              if (props.event.key === "ArrowUp") {
                selected = (selected - 1 + items.length) % items.length;
                paint();
                return true;
              }
              if (props.event.key === "Enter") {
                const item = items[selected];
                if (item) cmd?.(item);
                return true;
              }
              if (props.event.key === "Escape") {
                menu?.remove();
                menu = null;
                return true;
              }
              return false;
            },
            onExit: () => {
              menu?.remove();
              menu = null;
            },
          };
        },
      }),
    ];
  },
});
